import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Logos } from "../assets/logos";

export default function LoginPage() {
  const navigate = useNavigate();

  const { isLoggedIn, sendOtp, verifyOtp } = useAuth();

  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");

  const [otpSent, setOtpSent] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [timer, setTimer] = useState(0);

  /* ================================================= */
  /* Redirect if already logged in                     */
  /* ================================================= */

  useEffect(() => {
    if (isLoggedIn) {
      navigate("/dashboard", { replace: true });
    }
  }, [isLoggedIn, navigate]);

  /* ================================================= */
  /* OTP resend timer                                  */
  /* ================================================= */

  useEffect(() => {
    if (!otpSent || timer <= 0) return;

    const interval = setInterval(() => {
      setTimer((t) => t - 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [otpSent, timer]);

  /* ================================================= */
  /* Email validation                                  */
  /* ================================================= */

  const isValidEmail = (value) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  };

  /* ================================================= */
  /* Send OTP                                          */
  /* ================================================= */

  const handleSendOtp = async () => {
    if (!isValidEmail(email)) {
      setError("Please enter a valid email");
      return;
    }

    setError("");
    setLoading(true);

    try {
      await sendOtp(email);

      setOtpSent(true);
      setTimer(60);
    } catch (err) {
      setError(err.message || "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  /* ================================================= */
  /* Verify OTP                                        */
  /* ================================================= */

  const handleVerifyOtp = async () => {
    if (otp.length !== 6) {
      setError("OTP must be 6 digits");
      return;
    }

    setError("");
    setLoading(true);

    try {
      await verifyOtp(email, otp);

      navigate("/dashboard");
    } catch (err) {
      setError(err.message || "Invalid OTP");
    } finally {
      setLoading(false);
    }
  };

  /* ================================================= */
  /* OTP input sanitizer                               */
  /* ================================================= */

  const handleOtpChange = (e) => {
    const value = e.target.value.replace(/\D/g, "");
    setOtp(value);
  };

  /* ================================================= */
  /* UI                                                */
  /* ================================================= */

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">

      <div className="w-full max-w-md">

        {/* Logo */}

        <div className="mb-8 text-center">
          <img
            src={Logos.original.rectangle}
            alt="HeadwaySpot"
            className="mx-auto h-10 mb-3"
          />

          <h1 className="text-xl font-semibold text-gray-900">
            Welcome to HeadwaySpot
          </h1>
        </div>

        {/* Card */}

        <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">

          <h2 className="text-center text-lg font-medium mb-6">
            Sign in with your work email
          </h2>

          <div className="space-y-4">

            {/* Email */}

            <div>
              <label className="block text-sm text-gray-600 mb-1">
                Email
              </label>

              <input
                type="email"
                value={email}
                disabled={otpSent}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-200 disabled:bg-gray-100"
              />
            </div>

            {/* OTP */}

            {otpSent && (
              <div>

                <label className="block text-sm text-gray-600 mb-1">
                  One-Time Password
                </label>

                <input
                  type="text"
                  maxLength={6}
                  value={otp}
                  onChange={handleOtpChange}
                  placeholder="Enter 6-digit OTP"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-200"
                />

                <div className="mt-2 text-xs text-gray-500 flex justify-between">

                  {timer > 0 ? (
                    <span>Resend available in {timer}s</span>
                  ) : (
                    <button
                      onClick={handleSendOtp}
                      disabled={loading}
                      className="text-orange-600 hover:underline"
                    >
                      Resend OTP
                    </button>
                  )}

                </div>

              </div>
            )}

            {/* Error */}

            {error && (
              <div className="rounded-lg bg-rose-50 border border-rose-200 p-2 text-xs text-rose-700">
                {error}
              </div>
            )}

            {/* Primary CTA */}

            {!otpSent ? (
              <button
                onClick={handleSendOtp}
                disabled={loading || !email}
                className="w-full rounded-lg bg-gray-900 text-white py-2 text-sm font-medium hover:opacity-90 disabled:opacity-50"
              >
                {loading ? "Sending OTP…" : "Send OTP"}
              </button>
            ) : (
              <button
                onClick={handleVerifyOtp}
                disabled={loading || otp.length !== 6}
                className="w-full rounded-lg bg-[#F97316] text-white py-2 text-sm font-medium hover:opacity-90 disabled:opacity-50"
              >
                {loading ? "Verifying…" : "Verify & Continue"}
              </button>
            )}

          </div>

          {/* Divider */}

          <div className="flex items-center my-6">
            <div className="flex-1 h-px bg-gray-200" />
            <span className="px-3 text-xs text-gray-400">or</span>
            <div className="flex-1 h-px bg-gray-200" />
          </div>

          {/* Register */}

          <p className="text-sm text-center text-gray-600">
            Don’t have an account?{" "}
            <Link
              to="/register"
              className="text-orange-600 hover:underline"
            >
              Register
            </Link>
          </p>

        </div>

        {/* Footer */}

        <div className="mt-8 text-center text-xs text-gray-400 space-y-2">

          <div className="flex justify-center gap-4 flex-wrap">

            <a
              href="/contact-us"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-gray-600"
            >
              Contact Us
            </a>

            <a
              href="/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-gray-600"
            >
              Privacy Policy
            </a>

            <a
              href="/refund-cancellation"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-gray-600"
            >
              Refund & Cancellation
            </a>

            <a
              href="/terms-of-service"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-gray-600"
            >
              Terms & Conditions
            </a>

          </div>

          <p>
            © {new Date().getFullYear()} Funde Technologies
          </p>

        </div>

      </div>
    </div>
  );
}